import HeaderBrand from "@/src_new/shared/legacy/components/layout/Header/HeaderBrand";
import { getThemeComponentGroup } from "@/src_new/shared/lib/themeComponentClasses";
import { usePosterReducers } from "@/src_new/shared/redux/store/useGetData";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { navbarItems } from "../core/config";

export default function SideBar(): React.JSX.Element {
  const {
    auth: { user },
  } = usePosterReducers();
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const permissions = user?.user?.role?.permissions || {};
  const [isCollapsed, setIsCollapsed] = useState(false);
  const sidebarClasses = getThemeComponentGroup("sidebar");
  const sidebarBgClass = sidebarClasses?.bgColor || "bg-theme-accent";
  const sidebarTextClass = sidebarClasses?.textColor || "text-theme-text";
  const activeItemBgClass = sidebarClasses?.activeItemBgColor || "bg-theme-text/10";
  const activeItemTextClass = sidebarClasses?.activeItemTextColor || sidebarTextClass;
  const inactiveItemTextClass = sidebarClasses?.inactiveItemTextColor || "text-theme-text/60";
  const toggleButtonBgClass =
    sidebarClasses?.toggleButtonBgColor || "bg-theme-text/10 hover:bg-theme-text/20";
  const toggleIconClass = sidebarClasses?.toggleIconColor || sidebarTextClass;

  const moduleAliases: Record<string, string[]> = {
    insurerconfig: ["insurerconfig", "insurers", "insurer"],
    money: ["money", "commision", "commission", "commissions"],
  };

  const hasPermission = (module: string, action: string) => {
    const keysToCheck = moduleAliases[module] || [module];
    return keysToCheck.some((key) => permissions?.[key]?.includes(action));
  };

  const filteredNavItems = navbarItems.filter((item) => {
    if (!item.module || !item.action) return false;

    return hasPermission(item.module, item.action);
  });

  function getActiveTab() {
    const activeItem = filteredNavItems.find((item) => pathname.startsWith(item.path));
    return activeItem ? activeItem.id : "";
  }

  const callOnTabChange = (item) => {
    console.log("Tab changed to:", item);
    navigate(item.path);

    // Implement any additional logic needed when a tab is changed
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  console.log("User Permissions:", permissions, "Filtered Nav Items:", filteredNavItems);
  return (
    <aside
      className={`
         inset-y-0 left-0 z-40 transform transition-all duration-300 ease-in-out overflow-y-auto thin-scrollbar
        md:translate-x-0 border-r border-theme-border/50 flex flex-col
        ${sidebarBgClass} ${sidebarTextClass} ${sidebarClasses?.root || ""}
        ${isCollapsed ? "w-16" : "w-64"}
      `}
    >
      {/* Branch Header */}
      <div className={`mb-5 mt-5 ${isCollapsed ? "ml-2 mr-2 flex justify-center" : "ml-7"}`}>
        {!isCollapsed && <HeaderBrand variant="sidebar" />}
        {isCollapsed && (
          <div className="flex justify-center transform scale-50 origin-center">
            <HeaderBrand variant="sidebar" />
          </div>
        )}
      </div>

      <nav className="flex-1 pt-0">
        {filteredNavItems?.map((item) => (
          <button
            key={item.id}
            onClick={() => callOnTabChange(item)}
            className={`w-full flex items-center gap-3 py-4 transition-all 
              ${isCollapsed ? "justify-center px-2" : "px-6"}
              ${
                getActiveTab() === item.id
                  ? `${activeItemBgClass} border-r-4 border-current font-bold ${activeItemTextClass} ${sidebarClasses?.activeItem || ""}`
                  : `${inactiveItemTextClass} hover:bg-theme-text/5 ${sidebarClasses?.inactiveItem || ""}`
              }
              `}
          >
            <item.icon size={20} />
            {!isCollapsed && <span className="text-sm font-medium">{item.label}</span>}
          </button>
        ))}
      </nav>

      {/* Collapsible Button */}
      <div className={`py-4 ${isCollapsed ? "flex justify-center" : "flex justify-end pr-4"}`}>
        <button
          onClick={toggleSidebar}
          className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors ${toggleButtonBgClass} ${sidebarClasses?.toggleButton || ""}`}
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <ChevronRight size={20} className={toggleIconClass} />
          ) : (
            <ChevronLeft size={20} className={toggleIconClass} />
          )}
        </button>
      </div>
    </aside>
  );
}
